import AnnouncementController from '@/controllers/admin/announcements';
import {
  AnnouncementFilterCriteria,
  AnnouncementSearchItemDto,
  AnnouncementSortOptions
} from '@/interfaces/admin/announcement'
import { Module } from 'vuex';

export interface AnnouncementsState {
  error: string | null;
  items: AnnouncementSearchItemDto[];
  loading: boolean;
  matchCount: number | null;
  filter: AnnouncementFilterCriteria;
  pagination: {
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  selectedIds: number[];
  sort: AnnouncementSortOptions;
  totalCount: number;
}

const initialState: AnnouncementsState = {
  error: null,
  filter: { search: '' },
  items: [],
  loading: false,
  matchCount: null,
  pagination: {
    limit: 100,
    offset: 0,
    hasMore: false
  },
  selectedIds: [],
  sort: {
    field: 'fromDate',
    direction: 'desc'
  },
  totalCount: 0
};

// Create the module
const adminAnnouncements: Module<AnnouncementsState, any> = {
  namespaced: true,

  state: { ...initialState },

  getters: {
    // Get filtered and sorted announcements (UI representation)
    visibleAnnouncements: (state) => state.items,

    // Simple state accessors
    isLoading: state => state.loading,
    hasError: state => !!state.error,
    errorMessage: state => state.error,
    currentSort: state => state.sort,
    currentFilter: state => state.filter,
    selectedAnnouncements: state => state.selectedIds,
    canLoadMore: state => state.pagination.hasMore,
    totalCount: state => state.totalCount,
    matchCount: state => state.matchCount
  },

  mutations: {
    RECEIVE_ANNOUNCEMENTS(state, { items, totalCount, matchCount, reset = false }) {
      if (reset) {
        state.items = items;
      } else {
        state.items = [...state.items, ...items];
      }
      state.totalCount = totalCount;
      state.matchCount = matchCount;
    },

    START_LOADING(state) {
      state.loading = true;
      state.error = null;
    },

    FINISH_LOADING(state) {
      state.loading = false;
    },

    SET_ERROR(state, message) {
      state.error = message;
      state.loading = false;
    },

    UPDATE_SELECTION(state, ids) {
      state.selectedIds = ids;
    },

    UPDATE_FILTER(state, filter) {
      state.filter = { ...filter };
      state.pagination.offset = 0;
    },

    UPDATE_SORT(state, sort) {
      state.sort = { ...sort };
      state.pagination.offset = 0;
    },

    // Pagination mutations
    UPDATE_PAGINATION(state, { offset, limit, hasMore }) {
      state.pagination.offset = offset;
      state.pagination.limit = limit || state.pagination.limit;
      state.pagination.hasMore = hasMore;
    },

    RESET_STATE(state) {
      Object.assign(state, JSON.parse(JSON.stringify(initialState)));
    }
  },

  actions: {
    /**
     * Action: User loads the announcement page
     * Result: Initial data load
     */
    async initialize({ commit, dispatch }) {
      commit('RESET_STATE');
      await dispatch('loadAnnouncements', { reset: true });
    },

    /**
     * Action: User scrolls to bottom of list
     * Result: Load next page of data
     */
    async loadMore({ state, dispatch }) {
      if (!state.pagination.hasMore || state.loading) return;

      // Calculate next offset and load
      const nextOffset = state.pagination.offset + state.pagination.limit;
      await dispatch('loadAnnouncements', { offset: nextOffset });
    },

    /**
     * Action: User changes search term
     * Result: Apply filter and reload data
     */
    async updateSearch({ commit, dispatch }, searchTerm) {
      commit('UPDATE_FILTER', { search: searchTerm });
      await dispatch('loadAnnouncements', { reset: true });
    },

    /**
     * Action: User changes sort column or direction
     * Result: Apply sort and reload data
     */
    async updateSort({ commit, dispatch }, sort) {
      commit('UPDATE_SORT', sort);
      await dispatch('loadAnnouncements', { reset: true });
    },

    /**
     * Action: User selects/deselects announcements
     * Result: Update selection state
     */
    updateSelection({ commit }, selectedIds) {
      commit('UPDATE_SELECTION', selectedIds);
    },

    /**
     * Action: User requests to clear the current selection
     * Result: Clears the selection state
     */
    clearSelection({ commit }) {
      commit('UPDATE_SELECTION', []);
    },

    /**
     * Action: User requests to delete selected announcements
     * Result: Delete announcements and refresh list
     */
    async deleteSelected({ state, commit, dispatch }) {
      if (!state.selectedIds.length) return;

      try {
        commit('START_LOADING');
        await AnnouncementController.deleteAnnouncements(state.selectedIds);
        commit('UPDATE_SELECTION', []);
        await dispatch('loadAnnouncements', { reset: true });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to delete announcements';
        commit('SET_ERROR', errorMessage);
        throw error;
      }
    },

    /**
     * Core data loading function (not directly exposed to UI)
     * Used by other actions to fetch data based on current state
     */
    async loadAnnouncements({ state, commit }, { offset, reset = false } = {}) {
      try {
        commit('START_LOADING');

        // Determine offset to load
        const currentOffset = offset !== undefined ? offset : (reset ? 0 : state.pagination.offset);

        // Prepare filter criteria object
        const criteria: AnnouncementFilterCriteria = {
          limit: state.pagination.limit,
          offset: currentOffset,
          sortField: state.sort.field,
          sortDirection: state.sort.direction,
          search: state.filter.search
        };

        // Fetch data using controller with enhanced search endpoint
        const response = await AnnouncementController.getAnnouncements(criteria);
        const items = response.items || [];
        const totalCount = response.totalCount || 0;
        const matchCount = response.matchCount || 0;
        const limit = response.limit || state.pagination.limit;
        const responseOffset = response.offset || 0;

        // Update state
        commit('RECEIVE_ANNOUNCEMENTS', {
          items,
          totalCount,
          matchCount,
          reset
        });

        // Update pagination state
        const hasMore = items.length > 0 && responseOffset + items.length < totalCount;
        commit('UPDATE_PAGINATION', {
          offset: responseOffset,
          limit: limit,
          hasMore
        });

        commit('FINISH_LOADING');
        return { items, totalCount };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load announcements';
        commit('SET_ERROR', errorMessage);
        throw error;
      }
    }
  }
};

export default adminAnnouncements;
